import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useBudgetAssistantStore } from '@flows.cash/core'
import { useAuthStore } from '@/store/authStore'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { DollarSign, Info, TrendingUp } from 'lucide-react'

interface IncomeStepProps {
  onNext: () => void
  onSkip: () => void
}

const CURRENCIES = [
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
]

export function IncomeStep({ onNext, onSkip }: IncomeStepProps) {
  const { t } = useTranslation()
  const { user } = useAuthStore()
  const { responses, updateResponses, initializeSession } = useBudgetAssistantStore()
  
  const [monthlyIncome, setMonthlyIncome] = useState('')
  const [currency, setCurrency] = useState('USD')
  const [isValid, setIsValid] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  // Initialize session and load existing data on mount
  useEffect(() => {
    const initialize = async () => {
      if (user?.id) {
        await initializeSession(user.id)
      }
    }
    initialize()
  }, [user?.id, initializeSession])

  // Load existing responses into local state
  useEffect(() => {
    if (responses.monthlyIncome) {
      setMonthlyIncome(responses.monthlyIncome.toString())
    }
    if (responses.currency) {
      setCurrency(responses.currency)
    }
  }, [responses])

  useEffect(() => {
    const income = parseFloat(monthlyIncome)
    setIsValid(!isNaN(income) && income > 0)
  }, [monthlyIncome])

  const handleNext = async () => {
    if (isValid && user?.id) {
      setIsLoading(true)
      try {
        await updateResponses({
          monthlyIncome: parseFloat(monthlyIncome),
          currency: currency
        }, 'income')
        onNext()
      } catch (error) {
        console.error('Error saving income data:', error)
      } finally {
        setIsLoading(false)
      }
    }
  }

  const selectedCurrency = CURRENCIES.find(c => c.code === currency)

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div className="text-center space-y-3">
        <div className="relative mx-auto w-14 h-14 mb-3">
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 to-green-600 rounded-xl opacity-20 animate-pulse"></div>
          <div className="relative bg-gradient-to-r from-emerald-500 to-green-600 rounded-xl p-3 shadow-lg">
            <DollarSign className="h-8 w-8 text-white" />
          </div>
        </div>
        
        <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
          {t('budgetAssistant.income.title', 'What\'s your monthly income?')}
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 max-w-xl mx-auto">
          {t('budgetAssistant.income.subtitle', 'Tell us your total take-home income after taxes. This helps us create realistic budget recommendations.')}
        </p>
      </div>

      {/* Income Input Form */}
      <Card className="p-6 space-y-5">
        <div className="space-y-4">
          {/* Currency Selection */}
          <div className="space-y-2">
            <Label htmlFor="currency" className="text-sm font-medium">
              {t('budgetAssistant.income.currency', 'Currency')}
            </Label>
            <Select value={currency} onValueChange={setCurrency}>
              <SelectTrigger className="w-full h-10">
                <SelectValue placeholder="Select currency" />
              </SelectTrigger>
              <SelectContent>
                {CURRENCIES.map((curr) => (
                  <SelectItem key={curr.code} value={curr.code}>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{curr.symbol}</span>
                      <span>{curr.name}</span>
                      <span className="text-muted-foreground">({curr.code})</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Income Amount */}
          <div className="space-y-2">
            <Label htmlFor="income" className="text-sm font-medium">
              {t('budgetAssistant.income.amount', 'Monthly Income (After Tax)')}
            </Label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                {selectedCurrency?.symbol}
              </div>
              <Input
                id="income"
                type="number"
                placeholder="0.00"
                value={monthlyIncome}
                onChange={(e) => setMonthlyIncome(e.target.value)}
                className="pl-8 text-base h-11"
                min="0"
                step="0.01"
              />
            </div>
          </div>
        </div>

        {/* Help Text */}
        <Card className="p-4 bg-blue-50/50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800">
          <div className="flex items-start gap-3">
            <Info className="h-4 w-4 text-blue-600 mt-0.5" />
            <div className="flex-1 space-y-2">
              <h4 className="font-medium text-blue-900 dark:text-blue-100 text-sm">
                {t('budgetAssistant.income.helpTitle', 'What to include:')}
              </h4>
              <ul className="text-xs text-blue-700 dark:text-blue-300 space-y-1">
                <li>• {t('budgetAssistant.income.help.salary', 'Regular salary or wages (after tax)')}</li>
                <li>• {t('budgetAssistant.income.help.freelance', 'Freelance or contract income')}</li>
                <li>• {t('budgetAssistant.income.help.other', 'Other regular income sources')}</li>
              </ul>
            </div>
          </div>
        </Card>

        {/* Validation Message */}
        {monthlyIncome && !isValid && (
          <div className="text-sm text-red-600 dark:text-red-400">
            {t('budgetAssistant.income.validation', 'Please enter a valid income amount')}
          </div>
        )}
      </Card>

      {/* Income Insights */}
      {isValid && (
        <Card className="p-5 bg-gradient-to-r from-emerald-50 to-green-50 dark:from-emerald-950/20 dark:to-green-950/20 border-emerald-200 dark:border-emerald-800">
          <div className="flex items-center gap-3 mb-3">
            <TrendingUp className="h-4 w-4 text-emerald-600" />
            <h4 className="font-medium text-emerald-900 dark:text-emerald-100 text-sm">
              {t('budgetAssistant.income.insights.title', 'Income Insights')}
            </h4>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
            <div className="text-center">
              <div className="font-semibold text-emerald-800 dark:text-emerald-200 text-sm">
                {selectedCurrency?.symbol}{(parseFloat(monthlyIncome) * 0.5).toLocaleString()}
              </div>
              <div className="text-emerald-600 dark:text-emerald-400 text-xs">
                {t('budgetAssistant.income.insights.needs', 'Needs (50%)')}
              </div>
            </div>
            <div className="text-center">
              <div className="font-semibold text-emerald-800 dark:text-emerald-200 text-sm">
                {selectedCurrency?.symbol}{(parseFloat(monthlyIncome) * 0.3).toLocaleString()}
              </div>
              <div className="text-emerald-600 dark:text-emerald-400 text-xs">
                {t('budgetAssistant.income.insights.wants', 'Wants (30%)')}
              </div>
            </div>
            <div className="text-center">
              <div className="font-semibold text-emerald-800 dark:text-emerald-200 text-sm">
                {selectedCurrency?.symbol}{(parseFloat(monthlyIncome) * 0.2).toLocaleString()}
              </div>
              <div className="text-emerald-600 dark:text-emerald-400 text-xs">
                {t('budgetAssistant.income.insights.savings', 'Savings (20%)')}
              </div>
            </div>
          </div>
          <p className="text-xs text-emerald-700 dark:text-emerald-300 mt-3 text-center">
            {t('budgetAssistant.income.insights.description', 'Based on the popular 50/30/20 budgeting rule')}
          </p>
        </Card>
      )}

      {/* Action Buttons */}
      <div className="flex items-center justify-between pt-4">
        <Button
          variant="outline"
          onClick={onSkip}
          className="text-gray-600 border-gray-300 hover:bg-gray-50 h-10"
        >
          {t('common.skip', 'Skip')}
        </Button>
        
        <Button
          onClick={handleNext}
          disabled={!isValid || isLoading}
          className="bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white px-6 h-10"
        >
          {isLoading ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          ) : (
            t('common.continue', 'Continue')
          )}
        </Button>
      </div>
    </div>
  )
} 